'use client';

import { useState } from 'react';
import FullscreenMedia from './fullscreen-media';
import { pageCountLabel, type PagePreview } from '@/lib/page-previews';

/**
 * "Look inside": the watermarked page previews the seller's listing form made
 * from the product's PDF (src/lib/page-previews.ts), as a row of pages that
 * open in the gallery's fullscreen viewer. Each image carries its own size, so
 * the row is laid out before the pictures arrive and nothing shifts.
 */
export function LookInside({
  previews,
  pageCount,
  productName,
}: {
  previews: readonly PagePreview[];
  pageCount: number | null;
  productName: string;
}) {
  const [openAt, setOpenAt] = useState<number | null>(null);
  if (previews.length === 0) return null;
  return (
    <section aria-labelledby="look-inside-heading" className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="look-inside-heading" className="font-seller-heading">Look inside</h2>
        <p className="text-sm text-muted-foreground">
          {pageCount ? `${pageCountLabel(pageCount)}, ` : ''}
          {previews.length === 1 ? 'one page shown' : `${previews.length} pages shown`}, watermarked
        </p>
      </div>
      <ul className="flex gap-3 overflow-x-auto pb-2">
        {previews.map((preview, index) => (
          <li key={preview.url} className="shrink-0">
            <button
              type="button"
              onClick={() => setOpenAt(index)}
              className="block overflow-hidden rounded-md border bg-white transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Open page ${preview.page} of ${productName}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={preview.url}
                alt=""
                width={preview.width}
                height={preview.height}
                loading="lazy"
                decoding="async"
                className="h-44 w-auto sm:h-52"
              />
            </button>
            <p className="mt-1 text-center text-xs text-muted-foreground">Page {preview.page}</p>
          </li>
        ))}
      </ul>
      {openAt !== null && (
        <FullscreenMedia
          media={previews.map((preview) => ({ type: 'image' as const, src: preview.url }))}
          startIndex={openAt}
          onClose={() => setOpenAt(null)}
        />
      )}
    </section>
  );
}
